/**
 * Enables vertical scrolling only when an element's content overflows its
 * height. Re-evaluates on container resize and content-height changes (e.g.
 * accordions expanding) via a ResizeObserver, so it's resize-friendly.
 *
 * Bind the result to drive the scroll classes and the Lenis opt-out attribute:
 *   <div class="h-full" x-data="overflowScroll"
 *     :class="overflows && 'overflow-y-auto !overscroll-auto'"
 *     :data-lenis-prevent="overflows ? '' : null">
 *
 * Keep it on its own element so it never collides with an animation x-data on
 * the parent.
 *
 * Entrance animations (e.g. `fadeUp`, which starts descendants at `y: 28`)
 * leave a non-identity transform on the content while they play. A translated
 * descendant inflates the scroll container's scrollHeight, so a measure taken
 * mid-animation reads a false overflow — and because clearing a transform does
 * NOT trip the ResizeObserver, that false `overflows` never gets corrected,
 * turning the panel into a scroll trap. So we wait for all descendant
 * transforms to settle (i.e. items are fully faded in) before trusting a
 * measure, and only run that wait while the panel is on-screen.
 */
const IDENTITY_TRANSFORMS = new Set([
  'none',
  'matrix(1, 0, 0, 1, 0, 0)',
  'matrix3d(1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1)',
]);

export default () => ({
  overflows: false,
  observer: null,
  intersection: null,
  visible: false,
  raf: null,

  // True once no descendant carries an entrance transform anymore — i.e. every
  // item has finished fading in.
  settled() {
    return !Array.from(this.$el.querySelectorAll('*')).some(
      (node) => !IDENTITY_TRANSFORMS.has(getComputedStyle(node).transform)
    );
  },

  measure() {
    this.overflows = this.$el.scrollHeight > this.$el.clientHeight;
  },

  check() {
    // Off-screen panels can't trap scroll, and their entrance animations hold
    // the pre-animation transform indefinitely — skip them so we don't spin a
    // rAF loop forever.
    if (!this.visible) return;
    cancelAnimationFrame(this.raf);
    // Deadline guards against a persistent transform (looping marquee,
    // parallax) keeping us from ever measuring.
    const deadline = performance.now() + 1500;
    const tick = () => {
      if (this.settled() || performance.now() >= deadline) {
        this.measure();
        return;
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  },

  init() {
    this.observer = new ResizeObserver(() => this.check());
    this.observer.observe(this.$el);
    if (this.$el.firstElementChild) this.observer.observe(this.$el.firstElementChild);

    this.intersection = new IntersectionObserver((entries) => {
      this.visible = entries.some((entry) => entry.isIntersecting);
      if (this.visible) this.check();
    });
    this.intersection.observe(this.$el);
  },

  destroy() {
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
    this.intersection?.disconnect();
  },
});
